import Phaser from 'phaser';
import { useGameStore } from '@/ui/store/gameStore';
import { saveManager } from '@/save';
import {
  assetBundleManager,
  type InitialAssetPlan,
} from '@/game/assets/AssetBundleManager';

/**
 * PreloadScene — ładuje minimalny zestaw potrzebny do pierwszej sceny gry.
 *
 * Strategia: load() jest tolerancyjny — gdy plik nie istnieje, Phaser loguje
 * błąd ale nie crash'uje. Reszta kodu sprawdza `textures.exists(key)` i
 * fallback'uje na generated placeholder.
 *
 * Zakres startowy: wspólne ikony świata, bieżący biom, aktywny avatar i
 * wyposażone pety. Plaza, Sanktuarium, inne biomy, jajka i wyniki hatchu są
 * dociągane dopiero w chwili użycia.
 *
 * Co generuje proceduralnie (gdy PNG nie istnieje albo dla FX):
 *  - placeholder-* (kolorowe prostokąty)
 *  - fx-sparkle-star (24×24 czteroramienna iskra dla gold/rainbow petów)
 *  - fx-particle-yellow (16×16 kółko do coin burst)
 */
export class PreloadScene extends Phaser.Scene {
  private initialPlan?: InitialAssetPlan;

  constructor() {
    super({ key: 'PreloadScene' });
  }

  preload(): void {
    // ── Pasek postępu ────────────────────────────────────────────────────────
    const w = this.scale.width;
    const h = this.scale.height;
    const barW = Math.min(360, w * 0.7);
    const barH = 20;
    const barX = (w - barW) / 2;
    const barY = h / 2;

    const bg = this.add.rectangle(w / 2, barY, barW + 8, barH + 8, 0x000000, 0.2);
    const fill = this.add.rectangle(barX, barY, 0, barH, 0xffffff).setOrigin(0, 0.5);

    // Pasek Phasera (zielone tło) zostaje jako zapas, ale właściwy postęp
    // pokazuje React'owy splash z logiem gry — PreloadScene tylko raportuje
    // wartości do store'a, a App.tsx trzyma splash na wierzchu aż do 'complete'.
    const store = useGameStore.getState();
    store.setAssetLoadProgress(0);

    this.load.on('progress', (p: number) => {
      fill.width = barW * p;
      store.setAssetLoadProgress(p);
    });
    this.load.on('complete', () => {
      bg.destroy();
      fill.destroy();
      store.setAssetLoadProgress(1);
      store.setAssetsLoaded(true);
    });

    // Globalny handler błędów ładowania — pet/egg/dest może się nie wgrać, OK
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      console.warn(`[PreloadScene] Nie udało się załadować: ${file.key} (${file.url}). Użyję placeholder'a.`);
    });

    // Na starcie pobieramy wyłącznie rdzeń UI, aktywny biom oraz aktualnie
    // wybrane tekstury gracza. Pozostałe paczki ładuje AssetBundleManager
    // na żądanie pod osłoną TeleportScene / HatchScene.
    this.initialPlan = assetBundleManager.queueInitial(this, saveManager.getCurrent());
    console.info(
      `[PreloadScene] Kolejka startowa: ${this.initialPlan.assets.length} tekstur ` +
      `(${this.initialPlan.bundles.map((bundle) => bundle.id).join(', ')}).`,
    );
  }

  create(): void {
    // ── Placeholdery (fallback gdy PNG się nie wgra) ─────────────────────────
    this.makePlaceholderRect('placeholder-square', 64, 64, 0xff66cc);
    this.makePlaceholderRect('placeholder-coin-pile-s', 48, 36, 0xf1c40f);
    this.makePlaceholderRect('placeholder-coin-pile-m', 64, 48, 0xf1c40f);
    this.makePlaceholderRect('placeholder-coin-pile-l', 80, 64, 0xf39c12);
    this.makePlaceholderRect('placeholder-chest-wood', 64, 56, 0xc9885a);
    this.makePlaceholderRect('placeholder-chest-stone', 80, 72, 0xa8b0b3);
    this.makePlaceholderRect('placeholder-crystal', 64, 96, 0x7fe0b0);

    // Particle textures dla destruction FX (jeden preset = inny kolor cząstki)
    this.makeParticleTexture('fx-particle-yellow', 0xf1c40f, 0xd4a418);   // coin / złoto
    this.makeParticleTexture('fx-particle-brown',  0x8b5a3c, 0x4d2a14);   // drewno (splinter)
    this.makeParticleTexture('fx-particle-grey',   0xa8b0b3, 0x5d6669);   // kamień / kurz
    this.makeParticleTexture('fx-particle-cyan',   0x7fe0b0, 0x2b8e6e);   // kryształ
    this.makeParticleTexture('fx-particle-white',  0xffffff, 0xd0d0d0);   // generic dust / sparkle
    this.makeSparkleTexture('fx-sparkle-star');                            // wyraźna złota iskra gold/rainbow

    // Atmosfera świetlna (AtmosphereSystem) — proceduralne tekstury na BIAŁO,
    // barwa nakładana runtime przez setTint (jedna tekstura na wszystkie biomy).
    this.makeVignetteTexture();
    this.makeSunbeamTexture();

    // Raport dotyczy realnej, ograniczonej kolejki startowej — nie całego katalogu.
    const initialAssets = this.initialPlan?.assets ?? [];
    const loadedCount = initialAssets.filter((asset) => this.textures.exists(asset.key)).length;
    console.info(
      `[PreloadScene] Załadowano ${loadedCount}/${initialAssets.length} tekstur startowych.`,
    );

    // ── Biom 4 (Pixel World): NEAREST filter dla pixel-art tekstur ───────────
    // Globalny renderer ma antialias:true / pixelArt:false (Game.ts) — dobre dla
    // gładkich naklejek biomów 1-3, ale rozmazuje pixele biomu 4 przy skalowaniu.
    // Ustawiamy NEAREST PER-TEXTURE (nie globalnie), żeby pixel-art był ostry, a
    // naklejki 1-3 zostały gładkie. (Codex review biom4 LOW 2.) Wszystkie pixel-
    // biome textureKeys zawierają 'pixel' (pet_pixel_/bg_pixel/deco_pixel/
    // dest_pixel/fx_pixel/egg_pixel); żaden klucz biomów 1-3 go nie ma.
    assetBundleManager.applyTexturePolicies(this.textures, initialAssets);

    // Gwarancja: create() leci PO 'complete', więc grafiki są gotowe. Domknij
    // flagę na wypadek gdyby handler 'complete' nie wystartował (pusta kolejka).
    const store = useGameStore.getState();
    store.setAssetLoadProgress(1);
    store.setAssetsLoaded(true);

    this.scene.start('WorldScene');
  }

  private makePlaceholderRect(key: string, w: number, h: number, color: number): void {
    if (this.textures.exists(key)) return;
    const g = this.add.graphics();
    g.fillStyle(color, 1);
    g.fillRoundedRect(0, 0, w, h, 6);
    g.lineStyle(3, 0x000000, 1);
    g.strokeRoundedRect(0, 0, w, h, 6);
    g.generateTexture(key, w, h);
    g.destroy();
  }

  private makeParticleTexture(key: string, fillColor: number, outlineColor: number): void {
    if (this.textures.exists(key)) return;
    const g = this.add.graphics();
    g.fillStyle(fillColor, 1);
    g.fillCircle(8, 8, 7);
    g.lineStyle(2, outlineColor, 1);
    g.strokeCircle(8, 8, 7);
    g.generateTexture(key, 16, 16);
    g.destroy();
  }

  private makeSparkleTexture(key: string): void {
    if (this.textures.exists(key)) return;

    const g = this.add.graphics();
    const c = 12;

    g.fillStyle(0xffffff, 1);
    g.lineStyle(2, 0x7a4b00, 0.95);
    g.beginPath();
    g.moveTo(c, 1);
    g.lineTo(15, 9);
    g.lineTo(23, c);
    g.lineTo(15, 15);
    g.lineTo(c, 23);
    g.lineTo(9, 15);
    g.lineTo(1, c);
    g.lineTo(9, 9);
    g.closePath();
    g.fillPath();
    g.strokePath();

    g.fillStyle(0xfff3c0, 1);
    g.fillCircle(c, c, 3);
    g.generateTexture(key, 24, 24);
    g.destroy();
  }

  /**
   * Vignette dla AtmosphereSystem — biały radialny gradient: transparentny
   * środek → pełne krycie na rogach. `setTint` barwi go per-biom (white × tint).
   * Outer radius = size*0.5, ale rogi leżą dalej (size*0.707) → clamp do
   * ostatniego stopu = pełne zabarwienie rogów (pożądane).
   */
  private makeVignetteTexture(): void {
    if (this.textures.exists('fx_vignette')) return;
    const size = 256;
    const canvasTex = this.textures.createCanvas('fx_vignette', size, size);
    if (!canvasTex) return;
    const ctx = canvasTex.getContext();
    const c = size / 2;
    const grad = ctx.createRadialGradient(c, c, size * 0.22, c, c, size * 0.5);
    grad.addColorStop(0, 'rgba(255,255,255,0)');
    grad.addColorStop(0.55, 'rgba(255,255,255,0)');
    grad.addColorStop(1, 'rgba(255,255,255,1)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
    canvasTex.refresh();
  }

  /**
   * Sunbeam (god ray) dla AtmosphereSystem — biały snop: w poziomie jasny w osi
   * i wygaszony na bokach, w pionie wygaszony ku dalszemu końcowi (przez
   * destination-in alpha mask). `setTint` barwi per-biom, blend ADD/SCREEN
   * daje świetlisty glow.
   */
  private makeSunbeamTexture(): void {
    if (this.textures.exists('fx_sunbeam')) return;
    const bw = 128;
    const bl = 512;
    const canvasTex = this.textures.createCanvas('fx_sunbeam', bw, bl);
    if (!canvasTex) return;
    const ctx = canvasTex.getContext();

    // 1) Poziomy gradient: transparent → biały środek → transparent.
    const hg = ctx.createLinearGradient(0, 0, bw, 0);
    hg.addColorStop(0, 'rgba(255,255,255,0)');
    hg.addColorStop(0.5, 'rgba(255,255,255,1)');
    hg.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = hg;
    ctx.fillRect(0, 0, bw, bl);

    // 2) Wygaszenie wzdłuż długości (jasny koniec → zanik) maską alphy.
    ctx.globalCompositeOperation = 'destination-in';
    const vg = ctx.createLinearGradient(0, 0, 0, bl);
    vg.addColorStop(0, 'rgba(0,0,0,1)');
    vg.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, bw, bl);
    ctx.globalCompositeOperation = 'source-over';

    canvasTex.refresh();
  }
}
